/**
 * Progressive enhancement for the sign-up form. Without JavaScript the form still POSTs to
 * /api/signup and the server re-renders errors or redirects to /thank-you. With it:
 *   - phone auto-formats as (313) 555-0123 while typing
 *   - live character counter on the message
 *   - friendly inline validation on blur (not on every keystroke), linked via aria-describedby
 *   - an error summary that receives focus on a failed submit
 *   - fetch() submit with spinner -> check mark -> inline thank-you message
 *   - ?interest= and service-card links pre-fill the message
 * Rules mirror lib/validate.js (the server stays the source of truth).
 */
import { scrollToTarget } from './lenis.js';

const MIN_FILL_MS = 3200; // server rejects anything faster than 3s as a bot

const INTERESTS = {
  'personal-care': "I'm interested in Personal Care.",
  'everyday-living': "I'm interested in Everyday Living Support.",
  'medication-assistance': "I'm interested in Medication Assistance.",
  companionship: "I'm interested in Companionship.",
  'shared-living': "I'm interested in Shared Living.",
  'family-caregiver': "I'm caring for a family member at home and would like to learn how to get paid for it.",
};

const LABELS = { name: 'Full name', email: 'Email', phone: 'Phone', street: 'Street address', city: 'City', state: 'State', zip: 'ZIP code', message: 'Message', consent: 'Consent' };

const digits = (v) => String(v || '').replace(/\D/g, '');

export function formatPhone(value) {
  let d = digits(value);
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function validate(name, form) {
  const el = form.elements[name];
  if (!el) return '';
  const v = el.type === 'checkbox' ? el.checked : el.value.trim();
  switch (name) {
    case 'name':
      if (!v) return 'Please tell us your full name.';
      if (v.length < 2 || v.length > 80) return 'Your name should be between 2 and 80 characters.';
      return '';
    case 'email':
      if (!v) return 'Please enter your email address.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return 'That email address does not look quite right. Please check it.';
      return '';
    case 'phone': {
      const d = digits(v);
      if (!d) return 'Please enter a phone number so we can call you.';
      if (!(d.length === 10 || (d.length === 11 && d.startsWith('1')))) return 'Please enter a 10 digit US phone number, like (313) 555-0123.';
      return '';
    }
    case 'street': return v ? '' : 'Please enter your street address.';
    case 'city': return v ? '' : 'Please enter your city.';
    case 'zip': return /^\d{5}$/.test(v) ? '' : 'Please enter a 5 digit ZIP code.';
    case 'message':
      if (!v) return 'Please tell us a little about the care you are looking for.';
      if (v.length < 10) return 'Please write at least 10 characters so we understand what you need.';
      if (v.length > 1500) return 'Please keep your message under 1500 characters.';
      return '';
    case 'consent': return v ? '' : 'Please tick the box so we have your permission to contact you.';
    default: return '';
  }
}

function setError(form, name, message) {
  const wrap = form.querySelector(`[data-field="${name}"]`);
  const input = form.elements[name];
  if (!wrap || !input) return;
  wrap.classList.toggle('is-invalid', Boolean(message));
  const text = wrap.querySelector('[data-error-text]');
  if (text) text.textContent = message || '';
  if (message) input.setAttribute('aria-invalid', 'true');
  else input.removeAttribute('aria-invalid');
}

function showSummary(form, errors, heading) {
  const summary = form.querySelector('[data-error-summary]');
  const list = summary.querySelector('[data-error-list]');
  summary.querySelector('[data-error-summary-title]').textContent = heading || 'Please check the highlighted fields.';
  list.innerHTML = '';
  Object.entries(errors || {}).forEach(([name, msg]) => {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#field-${name}`;
    a.textContent = `${LABELS[name] || name}: ${msg}`;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      const input = form.elements[name];
      scrollToTarget(input.closest('.field') || input);
      setTimeout(() => input.focus(), 50);
    });
    li.appendChild(a);
    list.appendChild(li);
  });
  summary.hidden = false;
  summary.focus();
}

function prefill(form, interest) {
  if (!INTERESTS[interest]) return;
  const msg = form.elements.message;
  const current = msg.value.trim();
  // Only replace an empty box or another pre-filled sentence, never something the visitor wrote.
  if (!current || Object.values(INTERESTS).includes(current)) msg.value = INTERESTS[interest];
  form.elements.interest.value = interest;
  msg.dispatchEvent(new Event('input'));
}

export function initForms() {
  const loadedAt = Date.now();

  document.querySelectorAll('[data-signup-form]').forEach((form) => {
    const card = form.closest('[data-form-card]');
    const status = form.querySelector('[data-form-status]');
    const submit = form.querySelector('[data-submit]');
    const phone = form.querySelector('[data-phone]');
    const message = form.querySelector('[data-message]');
    const count = form.querySelector('[data-count]');
    const counter = form.querySelector('[data-counter]');
    const touched = new Set();

    // Phone formatting
    phone.addEventListener('input', () => {
      const atEnd = phone.selectionStart === phone.value.length;
      const formatted = formatPhone(phone.value);
      if (atEnd || formatted.length >= phone.value.length) phone.value = formatted;
    });
    if (phone.value) phone.value = formatPhone(phone.value);

    // Character counter
    const updateCount = () => {
      const n = message.value.length;
      count.textContent = n;
      counter.classList.toggle('is-near', n > 1350);
    };
    message.addEventListener('input', updateCount);
    updateCount();

    // Validate on blur; once a field has shown an error, re-check as they type so it clears.
    Object.keys(LABELS).forEach((name) => {
      const el = form.elements[name];
      if (!el) return;
      el.addEventListener('blur', () => {
        if (!el.value && !touched.has(name) && el.type !== 'checkbox') return; // don't scold a field just tabbed past
        touched.add(name);
        setError(form, name, validate(name, form));
      });
      el.addEventListener(el.type === 'checkbox' ? 'change' : 'input', () => {
        if (touched.has(name) || form.querySelector(`[data-field="${name}"]`)?.classList.contains('is-invalid')) {
          setError(form, name, validate(name, form));
        }
      });
    });

    // ?interest= pre-fill (the server also does this for no-JS visitors)
    const params = new URLSearchParams(location.search);
    if (params.get('interest')) prefill(form, params.get('interest'));

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      status.textContent = '';
      const errors = {};
      Object.keys(LABELS).forEach((name) => {
        const msg = validate(name, form);
        touched.add(name);
        setError(form, name, msg);
        if (msg) errors[name] = msg;
      });
      if (Object.keys(errors).length) {
        showSummary(form, errors);
        return;
      }
      form.querySelector('[data-error-summary]').hidden = true;

      submit.classList.add('is-loading');
      submit.disabled = true;
      form.setAttribute('aria-busy', 'true');
      status.textContent = 'Sending your message...';

      // A very quick human would otherwise trip the bot timer; just wait out the difference.
      const wait = MIN_FILL_MS - (Date.now() - loadedAt);
      if (wait > 0) await new Promise((r) => setTimeout(r, wait));

      try {
        const res = await fetch(form.action, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json', 'X-Requested-With': 'fetch' },
          body: new URLSearchParams(new FormData(form)),
          credentials: 'same-origin',
        });
        const data = await res.json().catch(() => ({}));

        if (res.ok && data.ok) {
          submit.classList.remove('is-loading');
          submit.classList.add('is-success');
          status.textContent = 'Message sent. Thank you.';
          setTimeout(() => showSuccess(card, data.firstName), 900);
          return;
        }

        submit.classList.remove('is-loading');
        submit.disabled = false;
        form.removeAttribute('aria-busy');
        status.textContent = '';
        if (data.errors) Object.entries(data.errors).forEach(([name, msg]) => setError(form, name, msg));
        showSummary(form, data.errors || {}, data.message || 'Something went wrong. Please try again, or email us directly.');
      } catch (err) {
        submit.classList.remove('is-loading');
        submit.disabled = false;
        form.removeAttribute('aria-busy');
        status.textContent = '';
        showSummary(form, {}, 'We could not reach our server. Please check your connection and try again, or email us directly.');
      }
    });
  });

  // Service-card links: stay on the page and pre-fill the form when it is here.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[data-interest]');
    const form = document.querySelector('[data-signup-form]');
    if (!link || !form || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    prefill(form, link.dataset.interest);
    scrollToTarget(document.getElementById('signup'));
  });
}

export function showSuccess(card, firstName) {
  const tpl = card.querySelector('[data-success-template]');
  const node = tpl.content.firstElementChild.cloneNode(true);
  node.querySelector('[data-first-name]').textContent = firstName || 'friend';
  card.querySelector('form').replaceWith(node);
  node.focus();
  // Draw the heart/home mark
  node.querySelectorAll('.mark-draw').forEach((p, i) => {
    p.style.transition = `stroke-dashoffset 1.1s cubic-bezier(.22,1,.36,1) ${0.15 + i * 0.2}s`;
    requestAnimationFrame(() => requestAnimationFrame(() => { p.style.strokeDashoffset = '0'; }));
  });
}
